import { Store, ReducersMapObject } from 'redux';
import { configReduxApp, thunkLoadingPlugin } from 'cosmos-redux';
import { setAutoFreeze } from 'immer';

import models from './models';

setAutoFreeze(false);
interface Action {
  type: string;
  now: string;
  payload: any;
}

export interface Model {
  state: any;
  reducers: any;
  actions: any;
}

export type ModelNames = keyof typeof models;

export type Models = {
  [Property in ModelNames]?: Model;
};

/**
 * isAsyncFn
 * @param {function} fn
 * @returns {boolean}
 */
const isAsyncFn = (fn: any) => {
  if (typeof fn !== 'function') return false;
  const str = fn.toString();
  return (
    str.includes('regeneratorRuntime.mark(') ||
    str.includes('_regenerator') ||
    str.includes('.apply(') ||
    str.includes('_fetch_') ||
    str.includes('("/')
  );
};

/**
 * createReducer
 * @param {string} name
 * @param {object} state
 * @param {object} reducers
 * @returns {Reducer}
 */

export const createReducer = (newModels: Models): ReducersMapObject<any, any> => {
  const enhanceReducers = (name: string, state: any, reducers: any) => (
    currentState = state,
    action: Action,
  ) => {
    const { type, now, payload } = action;
    const [namespace, reducerName] = type.split('/');
    if (namespace !== name) return currentState;
    if (reducerName === '$SET_LOADING') return { ...currentState, now };
    return reducers[reducerName](currentState, ...payload);
  };
  const rootReducers: ReducersMapObject<any, any> = {};
  Object.entries(newModels).forEach(([name, model]) => {
    if (model) {
      const { state, reducers } = model;
      rootReducers[name] = enhanceReducers(name, state, reducers);
    }
  });
  return rootReducers;
};

export const enhanceModels = (store: Store, newModels: Models) => {
  const { dispatch, getState } = store;
  // Methods
  Object.entries(newModels).forEach(([name, model]) => {
    if (model) {
      const { state, reducers, actions } = model;
      // reducers
      const newReducers: ReducersMapObject<any, any> = {};
      Object.keys(reducers).forEach((reducerName) => {
        newReducers[reducerName] = function reducer(...payload) {
          dispatch({ type: `${name}/${reducerName}`, payload });
        };
      });
      state.now = {};
      const setLoading = function reducer(actionName: string, loading: boolean) {
        dispatch({
          type: `${name}/$SET_LOADING`,
          now: { ...getState()[name].now, [actionName]: loading },
        });
      };
      // actions
      const newActions: any = {};
      const realActions = actions({ dispatch: {} });
      let memoActions: any = null;
      Object.keys(realActions).forEach((actionName) => {
        if (isAsyncFn(realActions[actionName])) {
          state.now[actionName] = false;
          newActions[actionName] = async function asyncAction(...args: any) {
            if (!memoActions) memoActions = actions({ dispatch, getState });
            setLoading(actionName, true);
            const data = await memoActions[actionName].bind({})(...args);
            setLoading(actionName, false);
            return data;
          };
        } else {
          newActions[actionName] = function action(...args: any) {
            if (!memoActions) memoActions = actions({ dispatch, getState });
            return memoActions[actionName].bind({})(...args);
          };
        }
      });
      (dispatch as any)[name] = { ...newReducers, ...newActions };
    }
  });
};

export const reduxApp = configReduxApp({
  reducer: createReducer(models),
  middleware: (getdefaultMiddlwares) =>
    getdefaultMiddlwares({ serializableCheck: false, immutableCheck: false }),
}).addPlugin(thunkLoadingPlugin);

const store = reduxApp.getStore();

// Methods
enhanceModels(store, models);

export default store;
export type RootState<T = {}> = T;
